'use client'

import { useState } from 'react'
import { formatDistanceToNow, differenceInDays } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { toast } from 'sonner'
import {
  Copy,
  Check,
  ExternalLink,
  Instagram,
  Package,
  ShoppingBag,
  Image,
  Clock,
  Tag,
  Mail,
  Phone,
  FileText,
  DollarSign,
  BarChart3,
  TrendingUp,
  RefreshCw,
  PauseCircle,
  Search,
  Info,
  Link2,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  useCreatorSalesSummary,
  useCreatorPostStats,
  useCreatorSeedings,
  useSyncCreatorCoupon,
  useLinkCreatorCoupon,
  useDeactivateCreatorCoupon,
  useCommissionTiers,
} from '@/hooks/use-creators'
import { Progress } from '@/components/ui/progress'
import type { CommissionTier } from '@/lib/api/types'
import type { CreatorWithTier } from '@/lib/api/modules/creators'
import type { Seeding } from '@/lib/api/types'
import type { ConfigDiffEntry } from '@/lib/types'
import { NotesLogEditor } from './notes-log'

// ─── Helpers ────────────────────────────────────────────────────────────────

const brl = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)

const brlCents = (cents: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100)

const compact = (n: number) => new Intl.NumberFormat('pt-BR', { notation: 'compact' }).format(n)

const relativeDate = (date: string | null | undefined) => {
  if (!date) return null
  return formatDistanceToNow(new Date(date), { addSuffix: true, locale: ptBR })
}

const CONTRACT_TYPE_LABELS: Record<string, string> = {
  commission: 'Comissão',
  barter: 'Permuta',
  fixed: 'Fixo Mensal',
  fixed_plus_commission: 'Fixo + Comissão',
}

const CREATOR_TYPE_LABELS: Record<string, string> = {
  creator: 'Creator',
  ugc_creator: 'UGC Creator',
  internal: 'Interno',
}

const SEEDING_STATUS_LABELS: Record<string, string> = {
  pending: 'Pendente',
  processing: 'Processando',
  shipped: 'Enviado',
  in_transit: 'Em Trânsito',
  delivered: 'Entregue',
  failed: 'Falhou',
  returned: 'Devolvido',
}

const SEEDING_STATUS_COLORS: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  processing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  shipped: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
  in_transit: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  delivered: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  returned: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
}

// ─── Sub-components ──────────────────────────────────────────────────────────

/** Animated copy button: shows Check for 2s after click */
function CopyButton({
  value,
  successMessage = 'Copiado!',
  className,
}: {
  value: string
  successMessage?: string
  className?: string
}) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(value)
    toast.success(successMessage)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={`h-7 w-7 shrink-0 transition-colors ${copied ? 'text-emerald-500' : 'text-muted-foreground hover:text-foreground'} ${className ?? ''}`}
            onClick={handleCopy}
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{copied ? 'Copiado!' : 'Copiar'}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

/** Stacked horizontal commission bar */
function CommissionBar({
  paid,
  approved,
  pending,
}: {
  paid: number
  approved: number
  pending: number
}) {
  const total = paid + approved + pending
  if (total === 0) {
    return <div className="h-3 w-full rounded-full bg-muted" />
  }

  const paidPct = (paid / total) * 100
  const approvedPct = (approved / total) * 100
  const pendingPct = (pending / total) * 100

  return (
    <div className="space-y-2.5">
      {/* Bar */}
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full bg-emerald-500 transition-all duration-700 ease-out"
          style={{ width: `${paidPct}%` }}
        />
        <div
          className="h-full bg-blue-500 transition-all duration-700 ease-out"
          style={{ width: `${approvedPct}%` }}
        />
        <div
          className="h-full bg-amber-400 transition-all duration-700 ease-out"
          style={{ width: `${pendingPct}%` }}
        />
      </div>
      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="inline-block h-2 w-2 rounded-sm bg-emerald-500" />
          <span>Pago</span>
          <span className="font-medium text-foreground">{brl(paid)}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="inline-block h-2 w-2 rounded-sm bg-blue-500" />
          <span>Aprovado</span>
          <span className="font-medium text-foreground">{brl(approved)}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="inline-block h-2 w-2 rounded-sm bg-amber-400" />
          <span>Pendente</span>
          <span className="font-medium text-foreground">{brl(pending)}</span>
        </div>
      </div>
    </div>
  )
}

/** Single seeding row in the mini-list */
function SeedingMiniRow({ seeding }: { seeding: Seeding }) {
  const productNames = seeding.products.map((p) => p.name).join(', ')
  const truncated = productNames.length > 48 ? productNames.slice(0, 45) + '…' : productNames

  return (
    <div className="flex items-start justify-between gap-3 py-2.5">
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="truncate text-sm font-medium leading-none">{truncated || 'Sem produtos'}</p>
        <p className="text-xs text-muted-foreground">
          {seeding.products.length} {seeding.products.length === 1 ? 'produto' : 'produtos'} ·{' '}
          {relativeDate(seeding.createdAt)}
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
            SEEDING_STATUS_COLORS[seeding.status] ?? 'bg-muted text-muted-foreground'
          }`}
        >
          {SEEDING_STATUS_LABELS[seeding.status] ?? seeding.status}
        </span>
        <span className="text-xs text-muted-foreground">{brl(seeding.totalCost)}</span>
      </div>
    </div>
  )
}

/** Tier progression display */
function TierProgressionDisplay({
  currentTier,
  totalSales,
  pendingSales,
  allTiers,
  tierOverride,
}: {
  currentTier: CommissionTier | null
  totalSales: number
  pendingSales: number
  allTiers: CommissionTier[]
  tierOverride: boolean
}) {
  if (!currentTier || allTiers.length === 0) {
    return <p className="text-sm text-muted-foreground">Nenhum tier configurado.</p>
  }

  const currentIdx = allTiers.findIndex((t) => t.position === currentTier.position)
  const nextTier = currentIdx < allTiers.length - 1 ? allTiers[currentIdx + 1] : null
  const isMaxTier = !nextTier

  const progressPct = nextTier
    ? Math.min(
        100,
        Math.max(
          0,
          ((totalSales - currentTier.minSalesCount) /
            (nextTier.minSalesCount - currentTier.minSalesCount)) *
            100
        )
      )
    : 100

  const remaining = nextTier ? Math.max(0, nextTier.minSalesCount - totalSales) : 0

  return (
    <div className="space-y-3">
      {/* Current tier + status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="default" className="text-xs">
            {currentTier.name}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {(currentTier.commissionRate * 100).toFixed(0)}% · {currentTier.monthlySeedings}{' '}
            seed/mês
          </span>
        </div>
        {tierOverride && (
          <Badge variant="outline" className="text-xs text-amber-600 dark:text-amber-400">
            Tier travado
          </Badge>
        )}
      </div>

      {/* Progress bar */}
      {!tierOverride && (
        <div className="space-y-1.5">
          <Progress value={progressPct} className="h-2" />
          <div className="flex items-center gap-1.5">
            <p className="text-xs text-muted-foreground">
              {isMaxTier ? (
                'Tier máximo atingido'
              ) : (
                <>
                  {totalSales} de {nextTier.minSalesCount} vendas aprovadas
                  {pendingSales > 0 && (
                    <span className="text-amber-600 dark:text-amber-400">
                      {' '}
                      ({pendingSales} pendente{pendingSales > 1 ? 's' : ''})
                    </span>
                  )}{' '}
                  — faltam <span className="font-medium text-foreground">{remaining}</span> para{' '}
                  <span className="font-medium text-foreground">{nextTier.name}</span>
                </>
              )}
            </p>
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3 w-3 text-muted-foreground/50 shrink-0 cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-55 text-xs">
                  Apenas vendas aprovadas (após período de garantia) contam para progressão de tier.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      )}

      {/* Tier ladder */}
      <div className="space-y-0">
        {allTiers.map((tier, idx) => {
          const isReached = tier.position <= currentTier.position
          const isCurrent = tier.position === currentTier.position
          return (
            <div
              key={tier.slug}
              className={`flex items-center gap-2.5 py-1.5 ${isCurrent ? 'font-medium' : ''}`}
            >
              <div
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                  isReached
                    ? 'bg-emerald-600 text-white'
                    : 'border border-muted-foreground/30 text-muted-foreground'
                }`}
              >
                {idx + 1}
              </div>
              <span
                className={`text-xs ${isCurrent ? 'text-foreground' : 'text-muted-foreground'}`}
              >
                {tier.name}
              </span>
              <span className="text-xs text-muted-foreground">
                {tier.minSalesCount}+ vendas · {(tier.commissionRate * 100).toFixed(0)}% ·{' '}
                {tier.monthlySeedings} seed
              </span>
              {isCurrent && (
                <span className="text-[10px] text-emerald-600 dark:text-emerald-400">← atual</span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────

interface CreatorOverviewTabProps {
  creator: CreatorWithTier
  onNavigateToSettings?: (scrollTo?: string) => void
}

export function CreatorOverviewTab({ creator, onNavigateToSettings }: CreatorOverviewTabProps) {
  const { data: summaryResult, isLoading: isLoadingSummary } = useCreatorSalesSummary(creator.id)
  const { data: statsResult, isLoading: isLoadingStats } = useCreatorPostStats(creator.id)
  const { data: seedingsResult, isLoading: isLoadingSeedings } = useCreatorSeedings(creator.id, {
    limit: 3,
  })

  const { data: allTiersData } = useCommissionTiers()

  const syncCoupon = useSyncCreatorCoupon()
  const linkCoupon = useLinkCreatorCoupon()
  const deactivateCoupon = useDeactivateCreatorCoupon()

  const [configDiffResult, setConfigDiffResult] = useState<ConfigDiffEntry[] | null>(null)
  const [diffSource, setDiffSource] = useState<'link' | 'sync' | null>(null)

  const hasBothPlatformsSynced = !!(creator.guruCouponId && creator.yampiCouponId)
  const hasAnySynced = !!(creator.guruCouponId || creator.yampiCouponId)
  const canSyncOrLink = !!(creator.couponCode && creator.programId)

  const handleLinkCoupon = () => {
    linkCoupon.mutate(
      { creatorId: creator.id, syncToProgram: false },
      {
        onSuccess: (result) => {
          if (result.configDiff?.length) {
            setConfigDiffResult(result.configDiff)
            setDiffSource('link')
          }
        },
      }
    )
  }

  const handleSyncCoupon = () => {
    syncCoupon.mutate(creator.id, {
      onSuccess: (result) => {
        if (result.configDiff?.length) {
          setConfigDiffResult(result.configDiff)
          setDiffSource('sync')
        }
      },
    })
  }

  const handleSyncFromDiffModal = () => {
    setConfigDiffResult(null)
    setDiffSource(null)
    syncCoupon.mutate(creator.id)
  }

  const handleCloseDiffModal = () => {
    setConfigDiffResult(null)
    setDiffSource(null)
  }

  // Unwrap the API wrapper — responses may be nested in `.data`
  const summary =
    (summaryResult as { data?: Record<string, number> })?.data ||
    (summaryResult as Record<string, number>)

  const stats =
    (statsResult as { data?: Record<string, unknown> })?.data ||
    (statsResult as Record<string, unknown>)

  const seedings: Seeding[] = (seedingsResult as Seeding[]) ?? []

  const allTiers = (
    ((allTiersData as { data?: unknown[] })?.data ?? allTiersData ?? []) as CommissionTier[]
  )
    .filter((t) => t.isActive)
    .sort((a, b) => a.position - b.position)

  const publicPageUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/creator/${creator.publicToken}`
      : `/creator/${creator.publicToken}`

  // Derived post stats
  const byType = (stats?.byType ?? {}) as Record<string, number>
  const totalPosts = (stats?.totalPosts as number) ?? 0
  const last30Days = (stats?.last30Days as number) ?? 0
  const lastPostDaysAgo = creator.lastPostAt
    ? differenceInDays(new Date(), new Date(creator.lastPostAt))
    : null

  // Commission values (in cents)
  const paid = (summary?.paidCommission as number) ?? 0
  const approved = (summary?.approvedCommission as number) ?? 0
  const pending = (summary?.pendingCommission as number) ?? 0
  const hasPending = pending > 0

  // Revenue breakdown
  const pendingAmount = (summary?.pendingAmount as number) ?? 0
  const approvedAmount = (summary?.approvedAmount as number) ?? 0

  // Contract + tier info
  const contractLabel = CONTRACT_TYPE_LABELS[creator.contractType ?? ''] ?? creator.contractType
  const creatorTypeLabel = CREATOR_TYPE_LABELS[creator.type ?? ''] ?? creator.type
  const commissionRate = creator.tierOverride
    ? null // override rate not directly available; tier has it
    : creator.commissionTier?.commissionRate

  return (
    <div className="space-y-6">
      {/* ── Row 1: KPI Cards ──────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {/* Total Vendas */}
        <Card className="border-l-4 border-l-blue-500 shadow-none">
          <CardContent className="p-4">
            {isLoadingSummary ? (
              <div className="space-y-2">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-7 w-14" />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <ShoppingBag className="h-3.5 w-3.5" />
                  Total Vendas
                </div>
                <p className="mt-1.5 text-2xl font-bold tabular-nums leading-none">
                  {compact((summary?.totalSales as number) ?? 0)}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {brl((summary?.totalSalesAmount as number) ?? 0)} gerados
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Receita Gerada */}
        <Card className="border-l-4 border-l-blue-400 shadow-none">
          <CardContent className="p-4">
            {isLoadingSummary ? (
              <div className="space-y-2">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-7 w-20" />
                <Skeleton className="h-3 w-32" />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <TrendingUp className="h-3.5 w-3.5" />
                  Receita Gerada
                </div>
                <p className="mt-1.5 text-2xl font-bold tabular-nums leading-none">
                  {brl((summary?.totalSalesAmount as number) ?? 0)}
                </p>
                {/* Pending vs approved revenue breakdown */}
                <div className="mt-1.5 flex items-center gap-2.5">
                  <span className="flex items-center gap-1 text-xs tabular-nums text-muted-foreground">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400" />
                    {brl(pendingAmount)}
                  </span>
                  <span className="flex items-center gap-1 text-xs tabular-nums text-muted-foreground">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    {brl(approvedAmount)}
                  </span>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Comissão */}
        <Card className="border-l-4 border-l-emerald-500 shadow-none">
          <CardContent className="p-4">
            {isLoadingSummary ? (
              <div className="space-y-2">
                <Skeleton className="h-3 w-28" />
                <Skeleton className="h-7 w-20" />
                <Skeleton className="h-3 w-32" />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <DollarSign className="h-3.5 w-3.5" />
                  Comissão
                </div>
                {/* approved + paid = total confirmed commission */}
                <p className="mt-1.5 text-2xl font-bold tabular-nums leading-none text-emerald-600 dark:text-emerald-400">
                  {brl(approved + paid)}
                </p>
                <div className="mt-1.5 flex items-center gap-2.5">
                  {pending > 0 && (
                    <span className="flex items-center gap-1 text-xs tabular-nums text-muted-foreground">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400" />
                      {brl(pending)}
                    </span>
                  )}
                  <span className="flex items-center gap-1 text-xs tabular-nums text-muted-foreground">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    pago: {brl(paid)}
                  </span>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Posts 30d */}
        <Card className="border-l-4 border-l-purple-500 shadow-none">
          <CardContent className="p-4">
            {isLoadingStats ? (
              <div className="space-y-2">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-7 w-10" />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <BarChart3 className="h-3.5 w-3.5" />
                  Posts (30d)
                </div>
                <p className="mt-1.5 text-2xl font-bold tabular-nums leading-none text-purple-600 dark:text-purple-400">
                  {last30Days}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">total: {totalPosts} posts</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Row 2: Two Columns ────────────────────────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-[1fr_minmax(0,_380px)]">
        {/* ── Left Column ── */}
        <div className="space-y-4 min-w-0">
          {/* Comissões por Status */}
          <Card className="shadow-none">
            <CardHeader className="pb-3 pt-4 px-4">
              <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                Comissões por Status
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {isLoadingSummary ? (
                <div className="space-y-2">
                  <Skeleton className="h-3 w-full rounded-full" />
                  <Skeleton className="h-3 w-3/4" />
                </div>
              ) : (
                <CommissionBar paid={paid} approved={approved} pending={pending} />
              )}
              {hasPending && !isLoadingSummary && (
                <div className="mt-3 flex items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                  <Clock className="h-3.5 w-3.5 shrink-0" />
                  <span>
                    <strong>{brl(pending)}</strong> em comissão pendente de aprovação
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Atividade de Conteúdo */}
          <Card className="shadow-none">
            <CardHeader className="pb-3 pt-4 px-4">
              <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                <Image className="h-4 w-4 text-muted-foreground" />
                Atividade de Conteúdo
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {isLoadingStats ? (
                <div className="space-y-3">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Post type breakdown */}
                  {totalPosts > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {byType.feed != null && byType.feed > 0 && (
                        <div className="flex items-center gap-1.5">
                          <Badge variant="secondary" className="gap-1">
                            <Image className="h-3 w-3" />
                            Feed
                          </Badge>
                          <span className="text-sm font-semibold">{byType.feed}</span>
                        </div>
                      )}
                      {byType.reel != null && byType.reel > 0 && (
                        <div className="flex items-center gap-1.5">
                          <Badge variant="secondary" className="gap-1">
                            <TrendingUp className="h-3 w-3" />
                            Reel
                          </Badge>
                          <span className="text-sm font-semibold">{byType.reel}</span>
                        </div>
                      )}
                      {byType.carousel != null && byType.carousel > 0 && (
                        <div className="flex items-center gap-1.5">
                          <Badge variant="secondary" className="gap-1">
                            <BarChart3 className="h-3 w-3" />
                            Carrossel
                          </Badge>
                          <span className="text-sm font-semibold">{byType.carousel}</span>
                        </div>
                      )}
                      {byType.story != null && byType.story > 0 && (
                        <div className="flex items-center gap-1.5">
                          <Badge variant="secondary" className="gap-1 text-xs">
                            Story
                          </Badge>
                          <span className="text-sm font-semibold">{byType.story}</span>
                        </div>
                      )}
                      {byType.tiktok != null && byType.tiktok > 0 && (
                        <div className="flex items-center gap-1.5">
                          <Badge variant="secondary" className="gap-1 text-xs">
                            TikTok
                          </Badge>
                          <span className="text-sm font-semibold">{byType.tiktok}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Nenhum post registrado</p>
                  )}

                  {/* Last post indicator */}
                  {creator.lastPostAt && lastPostDaysAgo != null && (
                    <div
                      className={`flex items-center gap-2 rounded-md px-3 py-2 text-xs ${
                        lastPostDaysAgo > 14
                          ? 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      <Clock className="h-3.5 w-3.5 shrink-0" />
                      <span>
                        Último post:{' '}
                        <strong>
                          há {lastPostDaysAgo === 0 ? 'hoje' : `${lastPostDaysAgo} dias`}
                        </strong>
                        {lastPostDaysAgo > 14 && ' — inativo há mais de 2 semanas'}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Seedings Recentes */}
          <Card className="shadow-none">
            <CardHeader className="pb-3 pt-4 px-4">
              <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                <Package className="h-4 w-4 text-muted-foreground" />
                Seedings Recentes
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {isLoadingSeedings ? (
                <div className="space-y-3">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="flex items-center justify-between gap-3 py-1">
                      <div className="flex-1 space-y-1.5">
                        <Skeleton className="h-3.5 w-48" />
                        <Skeleton className="h-3 w-28" />
                      </div>
                      <Skeleton className="h-5 w-20 rounded-full" />
                    </div>
                  ))}
                </div>
              ) : seedings.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-4 text-center">
                  <Package className="h-8 w-8 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">Nenhum seeding registrado</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {seedings.map((seeding) => (
                    <SeedingMiniRow key={seeding.id} seeding={seeding} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ── Right Column ── */}
        <div className="space-y-4">
          {/* Perfil Rápido */}
          <Card className="shadow-none">
            <CardHeader className="pb-3 pt-4 px-4">
              <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                <FileText className="h-4 w-4 text-muted-foreground" />
                Perfil Rápido
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 px-4 pb-4">
              {/* Contract + type */}
              <div className="flex flex-wrap items-center gap-1.5">
                {contractLabel && (
                  <Badge variant="outline" className="text-xs">
                    {contractLabel}
                  </Badge>
                )}
                {creatorTypeLabel && (
                  <Badge variant="secondary" className="text-xs">
                    {creatorTypeLabel}
                  </Badge>
                )}
                {creator.fixedValue != null && creator.fixedValue > 0 && (
                  <Badge
                    variant="outline"
                    className="text-xs text-emerald-700 dark:text-emerald-400"
                  >
                    Fixo {brlCents(creator.fixedValue)}
                  </Badge>
                )}
              </div>

              {/* Tier */}
              {creator.commissionTier && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Tier</span>
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium">{creator.commissionTier.name}</span>
                    <span className="text-xs text-muted-foreground">
                      ({(creator.commissionTier.commissionRate * 100).toFixed(0)}% ·{' '}
                      {creator.commissionTier.monthlySeedings} seed/mês)
                    </span>
                  </div>
                </div>
              )}
              {commissionRate != null && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Taxa comissão</span>
                  <span className="font-medium">{(commissionRate * 100).toFixed(1)}%</span>
                </div>
              )}

              {/* Niche */}
              {creator.niche && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Nicho</span>
                  <span className="font-medium capitalize">{creator.niche}</span>
                </div>
              )}

              {/* Tags */}
              {creator.tags && creator.tags.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Tag className="h-3 w-3" />
                    Tags
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {creator.tags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <Separator />

              {/* Contact */}
              <div className="space-y-2">
                {creator.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="flex-1 truncate font-mono text-xs">{creator.phone}</span>
                    <CopyButton value={creator.phone} successMessage="Telefone copiado!" />
                  </div>
                )}
                {creator.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="flex-1 truncate text-xs">{creator.email}</span>
                    <CopyButton value={creator.email} successMessage="E-mail copiado!" />
                  </div>
                )}
              </div>

              <Separator />

              {/* Socials */}
              <div className="space-y-2">
                {creator.instagramUsername ? (
                  <div className="flex items-center gap-2 text-sm">
                    <Instagram className="h-3.5 w-3.5 shrink-0 text-pink-500" />
                    <a
                      href={`https://instagram.com/${creator.instagramUsername}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-xs hover:underline hover:text-foreground transition-colors"
                    >
                      @{creator.instagramUsername}
                    </a>
                    {creator.instagramFollowers != null && (
                      <span className="text-xs font-medium text-muted-foreground">
                        {compact(creator.instagramFollowers)}
                      </span>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">Instagram não vinculado</p>
                )}
                {creator.tiktokUsername ? (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm bg-black text-[8px] font-bold text-white dark:bg-white dark:text-black">
                      TK
                    </span>
                    <a
                      href={`https://tiktok.com/@${creator.tiktokUsername}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-xs hover:underline hover:text-foreground transition-colors"
                    >
                      @{creator.tiktokUsername}
                    </a>
                    {creator.tiktokFollowers != null && (
                      <span className="text-xs font-medium text-muted-foreground">
                        {compact(creator.tiktokFollowers)}
                      </span>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">TikTok não vinculado</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Progressão de Tier */}
          <Card className="shadow-none">
            <CardHeader className="pb-3 pt-4 px-4">
              <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                Progressão de Tier
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {isLoadingSummary ? (
                <Skeleton className="h-24 w-full" />
              ) : (
                <TierProgressionDisplay
                  currentTier={creator.commissionTier as CommissionTier | null}
                  totalSales={(summary?.approvedSales as number) ?? 0}
                  pendingSales={(summary?.pendingSales as number) ?? 0}
                  allTiers={allTiers}
                  tierOverride={creator.tierOverride ?? false}
                />
              )}
            </CardContent>
          </Card>

          {/* Cupom & Links */}
          <Card className="shadow-none">
            <CardHeader className="pb-3 pt-4 px-4">
              <CardTitle className="text-sm font-semibold">Cupom & Links</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 px-4 pb-4">
              {/* Coupon code */}
              {creator.couponCode ? (
                <div className="space-y-1.5">
                  <p className="text-xs text-muted-foreground">Cupom</p>
                  <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2">
                    <span className="flex-1 font-mono text-sm font-semibold tracking-wider">
                      {creator.couponCode}
                    </span>
                    <CopyButton value={creator.couponCode} successMessage="Cupom copiado!" />
                  </div>

                  {/* Sync status badge */}
                  {creator.couponSyncStatus && creator.couponSyncStatus !== 'none' && (
                    <div className="flex items-center gap-1.5">
                      <Badge
                        variant={
                          creator.couponSyncStatus === 'synced'
                            ? hasAnySynced && !hasBothPlatformsSynced
                              ? 'outline'
                              : 'default'
                            : 'destructive'
                        }
                        className={`text-[10px] px-1.5 py-0 ${
                          hasAnySynced && !hasBothPlatformsSynced
                            ? 'border-amber-500 text-amber-700 dark:text-amber-400'
                            : ''
                        }`}
                      >
                        {creator.couponSyncStatus === 'synced'
                          ? hasAnySynced && !hasBothPlatformsSynced
                            ? 'Parcial'
                            : 'Sincronizado'
                          : 'Erro'}
                      </Badge>
                      {creator.couponSyncError && (
                        <span className="text-[10px] text-destructive truncate">
                          {creator.couponSyncError}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Coupon action buttons */}
                  {canSyncOrLink && (
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      <TooltipProvider delayDuration={300}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-[11px] px-2"
                              loading={syncCoupon.isPending}
                              onClick={handleSyncCoupon}
                            >
                              <RefreshCw className="mr-1 h-3 w-3" />
                              Atualizar nas Plataformas
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            Envia a config do programa para Guru e Yampi (cria se não existir)
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      {!hasBothPlatformsSynced && (
                        <TooltipProvider delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-[11px] px-2"
                                loading={linkCoupon.isPending}
                                onClick={handleLinkCoupon}
                              >
                                <Search className="mr-1 h-3 w-3" />
                                Buscar Existente
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              Para migração: busca o cupom pelo código nas plataformas sem
                              modificá-lo
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}

                      {hasAnySynced && (
                        <TooltipProvider delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-[11px] px-2 text-destructive hover:text-destructive"
                                loading={deactivateCoupon.isPending}
                                onClick={() => deactivateCoupon.mutate(creator.id)}
                              >
                                <PauseCircle className="mr-1 h-3 w-3" />
                                Desativar
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Desativar cupom nas plataformas</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Sem cupom configurado. Se já usa um código nos posts, configure-o em
                    Configurações e depois use &quot;Buscar Existente&quot; para vincular.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-[11px] px-2"
                    onClick={() => onNavigateToSettings?.('coupon-section')}
                  >
                    <Link2 className="mr-1 h-3 w-3" />
                    Configurar Cupom
                  </Button>
                </div>
              )}

              {/* Public page link */}
              {creator.publicToken && (
                <div className="space-y-1.5">
                  <p className="text-xs text-muted-foreground">Página pública</p>
                  <div className="flex items-center gap-1">
                    <div className="flex flex-1 items-center gap-2 overflow-hidden rounded-md bg-muted px-3 py-2">
                      <span className="flex-1 truncate text-xs text-muted-foreground">
                        {publicPageUrl}
                      </span>
                      <CopyButton value={publicPageUrl} successMessage="Link copiado!" />
                    </div>
                    <TooltipProvider delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-9 w-9 shrink-0"
                            asChild
                          >
                            <a href={publicPageUrl} target="_blank" rel="noreferrer">
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Abrir página pública</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notas */}
          <Card className="shadow-none">
            <CardHeader className="pb-3 pt-4 px-4">
              <CardTitle className="text-sm font-semibold">Observações</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <NotesLogEditor creatorId={creator.id} />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ── Row 3: Activity Timeline (desktop only) ───────────────────── */}
      <div className="hidden md:block">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-border/50 bg-muted/30 px-4 py-3">
          <TimelineItem
            icon={<Clock className="h-3.5 w-3.5" />}
            label="Criado em"
            value={relativeDate(creator.createdAt)}
          />
          {creator.lastContactAt && (
            <TimelineItem
              icon={<Phone className="h-3.5 w-3.5" />}
              label="Último contato"
              value={relativeDate(creator.lastContactAt)}
            />
          )}
          {creator.lastPostAt && (
            <TimelineItem
              icon={<Image className="h-3.5 w-3.5" />}
              label="Último post"
              value={relativeDate(creator.lastPostAt)}
            />
          )}
          <TimelineItem
            icon={<TrendingUp className="h-3.5 w-3.5" />}
            label="Atualizado"
            value={relativeDate(creator.updatedAt)}
          />
        </div>
      </div>

      {/* ── ConfigDiff Modal ─────────────────────────────────────────── */}
      <Dialog open={!!configDiffResult} onOpenChange={handleCloseDiffModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {diffSource === 'sync' ? 'Configurações atualizadas' : 'Configurações divergentes'}
            </DialogTitle>
            <DialogDescription>
              {diffSource === 'sync'
                ? 'As seguintes configurações foram atualizadas para alinhar com o programa:'
                : 'O cupom encontrado nas plataformas tem configurações diferentes do programa. Deseja atualizar para alinhar com o programa?'}
            </DialogDescription>
          </DialogHeader>

          {configDiffResult && (
            <div className="space-y-2">
              {configDiffResult.map((diff, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm"
                >
                  <div className="min-w-0 space-y-0.5">
                    <p className="font-medium capitalize">
                      {diff.field}{' '}
                      <span className="text-xs font-normal text-muted-foreground">
                        ({diff.platform})
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      <span className="text-destructive">{String(diff.currentValue)}</span>
                      {' → '}
                      <span className="text-emerald-600 dark:text-emerald-400">
                        {String(diff.programValue)}
                      </span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            {diffSource === 'sync' ? (
              <ChonkyButton onClick={handleCloseDiffModal}>Ok</ChonkyButton>
            ) : (
              <>
                <ChonkyButton variant="secondary" onClick={handleCloseDiffModal}>
                  Manter como está
                </ChonkyButton>
                <ChonkyButton onClick={handleSyncFromDiffModal} loading={syncCoupon.isPending}>
                  Sincronizar com programa
                </ChonkyButton>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ─── Timeline Item ───────────────────────────────────────────────────────────

function TimelineItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode
  label: string
  value: string | null
}) {
  if (!value) return null
  return (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <span className="opacity-60">{icon}</span>
      <span>{label}:</span>
      <span className="font-medium text-foreground/70">{value}</span>
    </div>
  )
}
